/* global __BUILD_ID__ */
import { useEffect, useRef, useState } from 'react';

// Injected at build time by Vite `define`. Falls back to 'dev' under the dev
// server, where no version.json is emitted and polling is skipped.
const CURRENT_BUILD = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev';

/**
 * Reports when a newer portal build has been deployed than the one this tab is
 * running. Polls /version.json every `intervalMs` and whenever the tab regains
 * focus, comparing the served buildId against this tab's build. No-op in dev.
 *
 * @param {number} intervalMs poll interval (default 60s)
 * @returns {boolean} true once a newer build is detected
 */
export function useBuildVersion(intervalMs = 60000) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (import.meta.env.DEV || CURRENT_BUILD === 'dev') return;
    let active = true;

    const check = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { buildId } = await res.json();
        if (active && buildId && buildId !== CURRENT_BUILD) {
          setUpdateAvailable(true);
        }
      } catch {
        /* transient network errors are ignored — retried on the next tick */
      } finally {
        inFlight.current = false;
      }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    check(); // initial probe

    return () => {
      active = false;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);

  return updateAvailable;
}
